import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useProjects } from '../hooks/useProjects';
import { useTasks } from '../hooks/useTasks';
import { Project, ProjectViewType } from '../types/project';
import CommonPageHeader from '../components/common/CommonPageHeader';
import ProjectBoardView from '../components/projects/ProjectBoardView';
import ProjectListView from '../components/projects/ProjectListView';
import ProjectBacklogView from '../components/projects/ProjectBacklogView';
import ProjectGanttView from '../components/projects/ProjectGanttView';
import ProjectCalendarView from '../components/projects/ProjectCalendarView';
import ProjectForm from '../components/projects/ProjectForm';
import LoadingScreen from '../components/common/LoadingScreen';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { 
  ChartBarIcon,
  Squares2X2Icon,
  ListBulletIcon,
  CalendarIcon,
  QueueListIcon,
  RectangleGroupIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ViewColumnsIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const ProjectBoard: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const navigate = useNavigate();
  const { projects, loading, createProject, updateProject, deleteProject } = useProjects();
  const { tasks } = useTasks();

  const [viewType, setViewType] = useState<ProjectViewType>('list');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [projectListView, setProjectListView] = useState<'grid' | 'list'>('grid');
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  type StatusFilter = 'current' | 'all' | Project['status'];
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => { try { return (localStorage.getItem('projectBoard.status') as StatusFilter) || 'current'; } catch { return 'current'; } });
  useEffect(() => { try { localStorage.setItem('projectBoard.status', statusFilter); } catch { /* ignore */ } }, [statusFilter]);
  const [search, setSearch] = useState('');

  // Jogosultság ellenőrzés
  if (!hasAccess('projects')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.errors.noPermission')}</div>
      </div>
    );
  }

  // Organization ellenőrzés
  if (!user?.organizationId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Nincs szervezet hozzárendelve</div>
      </div>
    );
  }

  if (loading) {
    return <LoadingScreen />;
  }

  // Aktív projektek szűrése és haladás számítás
  const activeProjects = projects.filter(p => p.isActive && !p.isArchived)
    .filter(p => statusFilter === 'all' ? true : statusFilter === 'current' ? (p.status !== 'completed' && p.status !== 'cancelled') : p.status === statusFilter)
    .filter(p => !search.trim() || (p.name || '').toLowerCase().includes(search.trim().toLowerCase()))
    .map(project => {
    // Projekt task-jainak szűrése
    const projectTasks = tasks.filter(t => t.projectId === project.id);
    
    // Haladás számítás task-ok alapján
    if (projectTasks.length === 0) {
      return { ...project, calculatedProgress: 0 };
    }
    
    const completedTasks = projectTasks.filter(t => t.status === 'completed').length;
    const calculatedProgress = Math.round((completedTasks / projectTasks.length) * 100);
    
    return { ...project, calculatedProgress };
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <CommonPageHeader
        title="Projekt Board"
        subtitle="Komplex projektmenedzsment"
        icon={ChartBarIcon}
      >
        <div className="flex gap-3">
          {/* Multi-projekt Gantt gomb */}
          <button
            onClick={() => navigate('/projects-gantt')}
            className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-md text-sm font-medium hover:bg-white/30 transition-colors flex items-center gap-2 border border-white/30"
          >
            <RectangleGroupIcon className="h-4 w-4" />
            <span>Összes Projekt Gantt</span>
          </button>

          {/* Új projekt gomb */}
          <button
            onClick={() => {
              setEditingProject(null);
              setShowProjectForm(true);
            }}
            className="px-4 py-2 bg-white text-primary-600 rounded-md text-sm font-medium hover:bg-white/90 transition-colors"
          >
            + Új projekt
          </button>

          {/* View Type Selector - csak ha van kiválasztott projekt */}
          {selectedProject && (
            <div className="flex gap-2 bg-white/20 backdrop-blur-sm rounded-lg p-1">
          <button
            onClick={() => setViewType('board')}
            className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
              viewType === 'board'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <Squares2X2Icon className="h-4 w-4" />
            <span>Board</span>
          </button>

          <button
            onClick={() => setViewType('list')}
            className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
              viewType === 'list'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <ListBulletIcon className="h-4 w-4" />
            <span>List</span>
          </button>

          <button
            onClick={() => setViewType('gantt')}
            className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
              viewType === 'gantt'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <ChartBarIcon className="h-4 w-4" />
            <span>Gantt</span>
          </button>

          <button
            onClick={() => setViewType('backlog')}
            className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
              viewType === 'backlog'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <QueueListIcon className="h-4 w-4" />
            <span>Backlog</span>
          </button>

          <button
            onClick={() => setViewType('calendar')}
            className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
              viewType === 'calendar'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <CalendarIcon className="h-4 w-4" />
            <span>Calendar</span>
          </button>
          </div>
          )}
        </div>
      </CommonPageHeader>

      <div className="w-full px-6 py-6">
        {/* Projekt választó - Kártyás nézet */}
        {!selectedProject ? (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Válassz projektet
                <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">({activeProjects.length})</span>
              </h3>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Keresés…"
                  className="crm-input-compact w-36 sm:w-44 border rounded-md px-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                  className="crm-input-compact border rounded-md px-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                  title="Projekt státusz szűrő"
                >
                  <option value="current">Aktuális (nem lezárt)</option>
                  <option value="active">Aktív</option>
                  <option value="planning">Tervezés</option>
                  <option value="paused">Szünetel</option>
                  <option value="completed">Befejezett</option>
                  <option value="cancelled">Törölt</option>
                  <option value="all">Összes</option>
                </select>
                <button
                  onClick={() => setProjectListView('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    projectListView === 'grid'
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                  title="Kártya nézet"
                >
                  <Squares2X2Icon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setProjectListView('list')}
                  className={`p-2 rounded-md transition-colors ${
                    projectListView === 'list'
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                  title="Lista nézet"
                >
                  <ListBulletIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            {projectListView === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {activeProjects.map(project => (
                <div
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-lg border-2 border-gray-200/50 dark:border-gray-700/50 p-6 hover:border-primary-500 hover:shadow-2xl hover:scale-105 hover:bg-white dark:hover:bg-gray-800 transition-all duration-200 group relative overflow-hidden cursor-pointer"
                >
                  {/* Gradient overlay */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div 
                      className="flex items-center gap-3 flex-1"
                    >
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                        style={{ 
                          background: project.color 
                            ? `linear-gradient(135deg, ${project.color} 0%, ${project.color}dd 100%)`
                            : 'linear-gradient(135deg, #9333EA 0%, #7E22CE 100%)'
                        }}
                      >
                        {project.icon ? (
                          <span className="text-2xl">{project.icon}</span>
                        ) : (
                          <span className="text-white font-bold text-base">{project.key}</span>
                        )}
                      </div>
                      <div>
                        <h4 className="font-bold text-base text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                          {project.name}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            project.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                            project.status === 'planning' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                            project.status === 'completed' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' :
                            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                          }`}>
                            {project.status === 'active' ? 'Aktív' :
                             project.status === 'planning' ? 'Tervezés' :
                             project.status === 'completed' ? 'Befejezett' : 'On Hold'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-start gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingProject(project);
                          setShowProjectForm(true);
                        }}
                        className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                        title="Szerkesztés"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingProject(project);
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Törlés"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 min-h-[40px]">
                    {project.description || 'Nincs leírás'}
                  </p>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <ClockIcon className="h-4 w-4 text-primary-500" />
                      <span>{project.startDate ? new Date(project.startDate).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }) : 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <UserGroupIcon className="h-4 w-4 text-primary-500" />
                      <span>{project.assignedUsers?.length || 0} tag</span>
                    </div>
                  </div>
                  
                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-gray-400 font-medium flex items-center gap-1">
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        Haladás
                      </span>
                      <span className="text-gray-900 dark:text-gray-100 font-bold">
                        {project.calculatedProgress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${project.calculatedProgress}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            ) : (
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-b-2 border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Projekt</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Státusz & Prioritás</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Időzítés</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Csapat</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Haladás</th>
                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Műveletek</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {activeProjects.map(project => (
                    <tr
                      key={project.id}
                      onClick={() => setSelectedProject(project)}
                      className="hover:bg-primary-50/50 dark:hover:bg-primary-900/10 transition-all group cursor-pointer"
                    >
                      {/* Projekt */}
                      <td className="px-6 py-5">
                        <div 
                          className="flex items-center"
                        >
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform"
                            style={{ 
                              background: project.color 
                                ? `linear-gradient(135deg, ${project.color} 0%, ${project.color}dd 100%)`
                                : 'linear-gradient(135deg, #9333EA 0%, #7E22CE 100%)'
                            }}
                          >
                            {project.icon ? (
                              <span className="text-2xl">{project.icon}</span>
                            ) : (
                              <span className="text-white font-bold text-sm">{project.key}</span>
                            )}
                          </div>
                          <div className="ml-4 flex-1">
                            <div className="text-sm font-bold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                              {project.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                              {project.description || 'Nincs leírás'}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      {/* Státusz & Prioritás */}
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-2 items-start">
                          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                            project.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                            project.status === 'planning' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                            project.status === 'completed' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' :
                            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                          }`}>
                            {project.status === 'active' ? '✓ Aktív' :
                             project.status === 'planning' ? '○ Tervezés' :
                             project.status === 'completed' ? '✓ Befejezett' : '⏸ On Hold'}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            project.priority === 'urgent' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                            project.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                            project.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                            'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          }`}>
                            {project.priority === 'urgent' ? '🔥 Sürgős' :
                             project.priority === 'high' ? '⬆️ Magas' :
                             project.priority === 'medium' ? '➡️ Közepes' : '⬇️ Alacsony'}
                          </span>
                        </div>
                      </td>
                      
                      {/* Időzítés */}
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-2 text-xs">
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <CalendarIcon className="h-4 w-4 text-primary-500" />
                            <span className="font-medium">
                              {project.startDate ? new Date(project.startDate).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }) : 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <ClockIcon className="h-4 w-4 text-primary-500" />
                            <span className="font-medium">
                              {project.endDate ? new Date(project.endDate).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }) : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </td>
                      
                      {/* Csapat */}
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <div className="flex -space-x-2">
                            {project.assignedUsersNames?.slice(0, 3).map((name, idx) => (
                              <div
                                key={idx}
                                className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-xs font-semibold border-2 border-white dark:border-gray-800 shadow-sm"
                                title={name}
                              >
                                {name.charAt(0).toUpperCase()}
                              </div>
                            ))}
                            {(project.assignedUsers?.length || 0) > 3 && (
                              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 text-xs font-semibold border-2 border-white dark:border-gray-800">
                                +{(project.assignedUsers?.length || 0) - 3}
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                            {project.assignedUsers?.length || 0} tag
                          </span>
                        </div>
                      </td>
                      
                      {/* Haladás */}
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400 font-medium">Készültség</span>
                            <span className="text-gray-900 dark:text-gray-100 font-bold">
                              {project.calculatedProgress}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden shadow-inner">
                            <div
                              className="bg-gradient-to-r from-primary-500 to-primary-600 h-2.5 rounded-full transition-all duration-500 shadow-sm"
                              style={{ width: `${project.calculatedProgress}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      
                      {/* Műveletek */}
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingProject(project);
                              setShowProjectForm(true);
                            }}
                            className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                            title="Szerkesztés"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingProject(project);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Törlés"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        ) : (
          <div className="mb-6">
            <button
              onClick={() => setSelectedProject(null)}
              className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium mb-4 flex items-center gap-2"
            >
              ← Vissza a projektekhez
            </button>
          </div>
        )}

        {/* View Content */}
        {!selectedProject ? (
          <div className="text-center py-12">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">Nincs projekt kiválasztva</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Válassz ki egy projektet a fenti listából a board megtekintéséhez.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {/* Projekt info */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{selectedProject.name}</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{selectedProject.description}</p>
              <div className="flex gap-4 mt-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Kulcs: <span className="font-semibold">{selectedProject.key}</span>
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Státusz: <span className="font-semibold">{selectedProject.status}</span>
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Haladás: <span className="font-semibold">{selectedProject.progress}%</span>
                </span>
              </div>
            </div>

            {/* View alapján különböző tartalom */}
            {viewType === 'board' && (
              <ProjectBoardView project={selectedProject} />
            )}

            {viewType === 'list' && (
              <ProjectListView project={selectedProject} />
            )}

            {viewType === 'gantt' && (
              <ProjectGanttView project={selectedProject} />
            )}

            {viewType === 'backlog' && (
              <ProjectBacklogView project={selectedProject} />
            )}

            {viewType === 'calendar' && (
              <ProjectCalendarView project={selectedProject} />
            )}
          </div>
        )}

        {/* Üres állapot */}
        {activeProjects.length === 0 && (
          <div className="text-center py-12">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nincs aktív projekt</h3>
            <p className="mt-1 text-sm text-gray-500">
              Hozz létre egy új projektet a kezdéshez.
            </p>
          </div>
        )}
      </div>

      {/* Projekt Form Modal */}
      <ProjectForm
        isOpen={showProjectForm}
        onClose={() => {
          setShowProjectForm(false);
          setEditingProject(null);
        }}
        onSubmit={async (data) => {
          try {
            if (editingProject) {
              await updateProject(editingProject.id, data);
              toast.success('Projekt frissítve!');
            } else {
              await createProject(data);
              toast.success('Projekt létrehozva!');
            }
            setShowProjectForm(false);
            setEditingProject(null);
          } catch (error) {
            console.error('Error saving project:', error);
            toast.error('Hiba történt a projekt mentése során');
          }
        }}
        initialData={editingProject || undefined}
      />

      {/* Delete Confirmation Dialog */}
      {deletingProject && (
        <ConfirmDialog
          isOpen={!!deletingProject}
          onClose={() => setDeletingProject(null)}
          onConfirm={async () => {
            try {
              await deleteProject(deletingProject.id);
              setDeletingProject(null);
              toast.success('Projekt törölve!');
            } catch (error) {
              console.error('Error deleting project:', error);
              toast.error('Hiba történt a projekt törlése során');
            }
          }}
          title="Projekt törlése"
          message={`Biztosan törölni szeretnéd ezt a projektet? Ez törli az összes hozzá tartozó feladatot is!`}
          itemName={deletingProject.name}
          confirmText="Törlés"
          cancelText="Mégse"
          type="danger"
        />
      )}
    </div>
  );
};

export default ProjectBoard;
